import { useState } from 'react';
import { Eye } from 'lucide-react';
import type {
  FillInBlankExerciseSubmission,
  MultipleChoiceExerciseSubmission,
  SelectForBlankExerciseSubmission,
} from '@org/utils-client';
import { Badge } from '../../../core/badge';
import { FilePreviewDialog, type PreviewableFile } from './file-preview-dialog';
import type { ExerciseSubmission } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import {
  extractEvalSentences,
  formatFileSize,
  formatOptionLetter,
  getFileTypeIcon,
  resolveGradeStatusTheme,
  resolveSelectedIndicesFromSubmission,
  sortByOrder,
} from './exercise-submission-helpers';
import { useUserType } from '../../../../feature-flags/use-user-type';

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

export function SubmissionContentRenderer({
  submission,
  content,
  evaluation,
}: {
  submission: ExerciseSubmission;
  content: StudentExerciseContent;
  evaluation: Record<string, unknown> | null;
}) {
  if (!submission || typeof submission !== 'object') {
    return <p className="text-sm text-gray-700">Envio nao disponivel.</p>;
  }

  switch (submission.type) {
    case 'MultipleChoice':
      return <MultipleChoiceSubmissionView submission={submission} content={content} />;
    case 'LongAnswer':
    case 'ShortAnswer':
      return <TextAnswerView answer={submission.answer} />;
    case 'Recorded':
      return <RecordedSubmissionView submission={submission} />;
    case 'FillInBlank':
      return <FillInBlankSubmissionView submission={submission} content={content} evaluation={evaluation} />;
    case 'SelectForBlank':
      return <SelectForBlankSubmissionView submission={submission} content={content} evaluation={evaluation} />;
    case 'FileUpload':
      return <FileUploadSubmissionView submission={submission as any} />;
    default:
      return (
        <pre className="whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs text-gray-700">
          {JSON.stringify(submission, null, 2)}
        </pre>
      );
  }
}

// ---------------------------------------------------------------------------
// Text answer (LongAnswer / ShortAnswer)
// ---------------------------------------------------------------------------

function TextAnswerView({ answer }: { answer?: string | null }) {
  return (
    <div className="space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Resposta</p>
      <p className="whitespace-pre-wrap">{answer ?? '\u2014'}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recorded
// ---------------------------------------------------------------------------

function RecordedSubmissionView({ submission }: { submission: { recordingUrl?: string; transcript?: string } }) {
  return (
    <div className="space-y-1 text-sm text-gray-700">
      {submission.recordingUrl ? (
        <audio controls src={submission.recordingUrl} className="w-full" />
      ) : (
        <p>Nenhuma gravacao enviada.</p>
      )}
      {submission.transcript && (
        <div className="space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Transcricao</p>
          <p className="whitespace-pre-wrap">{submission.transcript}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multiple Choice
// ---------------------------------------------------------------------------

function MultipleChoiceSubmissionView({
  submission,
  content,
}: {
  submission: MultipleChoiceExerciseSubmission;
  content: StudentExerciseContent;
}) {
  const options = sortByOrder(content.options ?? []);
  const selectedIndices = resolveSelectedIndicesFromSubmission(submission, options);
  const correctOptions = options.filter((option) => option.isCorrect);
  const selectedLabels = selectedIndices.length
    ? selectedIndices
        .map((index) => options[index]?.label ?? `Opcao ${formatOptionLetter(index)}`)
        .join(', ')
    : '\u2014';

  return (
    <div className="space-y-1 text-sm text-gray-700">
      <p>
        Opcao selecionada:{' '}
        <span className="font-medium text-gray-900">{selectedLabels}</span>
      </p>
      <p className="text-xs text-gray-500">
        Resposta correta:{' '}
        {correctOptions.length ? correctOptions.map((option) => option.label).join(', ') : '\u2014'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fill-in-blank
// ---------------------------------------------------------------------------

function FillInBlankSubmissionView({
  submission,
  content,
  evaluation,
}: {
  submission: FillInBlankExerciseSubmission;
  content: StudentExerciseContent;
  evaluation: Record<string, unknown> | null;
}) {
  const sentences = content.sentences ?? [];
  const evalSentences = extractEvalSentences(evaluation, 'FillInBlank');

  return (
    <div className="space-y-2 text-sm text-gray-700">
      {sentences.map((sentence, index) => {
        const blanks = sentence.blanks ?? [];
        const provided = submission.answers?.[index];
        const providedAnswers = Array.isArray(provided) ? provided : [provided];
        const fallbackBlank = { answer: (sentence as any)?.answer ?? '', blankId: undefined };
        const normalizedBlanks = blanks.length ? blanks : [fallbackBlank];
        const evalBlanks = Array.isArray(evalSentences[index]?.blanks) ? evalSentences[index].blanks : [];

        return (
          <div
            key={sentence.sentenceId ?? `${content.id}-sentence-${index}`}
            className="space-y-2 rounded-md border border-gray-200 bg-white p-3"
          >
            <SentenceHeader index={index} sentence={sentence.sentence} title={content.title} />
            <div className="space-y-2">
              {normalizedBlanks.map((blank, blankIdx) => {
                const evalBlank = evalBlanks[blankIdx] ?? {};
                const expectedAnswer =
                  typeof evalBlank.expectedAnswer === 'string'
                    ? evalBlank.expectedAnswer
                    : typeof blank.answer === 'string'
                      ? blank.answer
                      : '';
                const providedAnswer =
                  typeof evalBlank.providedAnswer === 'string'
                    ? evalBlank.providedAnswer
                    : (providedAnswers[blankIdx] ?? '');
                const grade = (evalBlank.grade as string | undefined) ?? (evalBlank.isCorrect ? 'perfect' : undefined);
                const isCorrect = Boolean(evalBlank.isCorrect ?? grade === 'perfect');
                const reason =
                  typeof evalBlank.reason === 'string' && evalBlank.reason.trim().length > 0 ? evalBlank.reason : null;

                return (
                  <BlankResultCard
                    key={blank.blankId ?? `${sentence.sentenceId ?? index}-blank-${blankIdx}`}
                    blankIndex={blankIdx}
                    providedAnswer={String(providedAnswer || '\u2014')}
                    expectedAnswer={expectedAnswer || '\u2014'}
                    grade={grade}
                    isCorrect={isCorrect}
                    reason={reason}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Select-for-blank
// ---------------------------------------------------------------------------

function SelectForBlankSubmissionView({
  submission,
  content,
  evaluation,
}: {
  submission: SelectForBlankExerciseSubmission;
  content: StudentExerciseContent;
  evaluation: Record<string, unknown> | null;
}) {
  const sentences = content.sentences ?? [];
  const evalSentences = extractEvalSentences(evaluation, 'SelectForBlank');

  return (
    <div className="space-y-3 text-sm text-gray-700">
      {sentences.map((sentence, sentenceIdx) => {
        const blanks = sentence.blanks ?? [];
        const selections = submission.selections?.[sentenceIdx] ?? [];
        const evalBlanks = Array.isArray(evalSentences[sentenceIdx]?.blanks) ? evalSentences[sentenceIdx].blanks : [];

        return (
          <div
            key={sentence.sentenceId ?? `${content.id}-select-${sentenceIdx}`}
            className="rounded-md border border-gray-200 bg-white p-3 space-y-2"
          >
            <SentenceHeader index={sentenceIdx} sentence={sentence.sentence} title={content.title} />
            <div className="space-y-2">
              {blanks.map((blank, blankIdx) => {
                const options = sortByOrder(blank.options ?? []);
                const selectedIndex = selections[blankIdx] ?? null;
                const selected =
                  selectedIndex !== null && selectedIndex >= 0 ? options[selectedIndex] ?? null : null;
                const correct = options.find((option) => option.isCorrect) ?? null;

                const evalBlank = evalBlanks[blankIdx] ?? {};
                const evalSelectedIndex =
                  typeof evalBlank.selectedOptionIndex === 'number' ? evalBlank.selectedOptionIndex : selectedIndex;
                const evalCorrectIndex =
                  typeof evalBlank.correctOptionIndex === 'number'
                    ? evalBlank.correctOptionIndex
                    : correct
                      ? options.findIndex((opt) => opt.optionId === correct.optionId)
                      : null;
                const isCorrect =
                  typeof evalBlank.isCorrect === 'boolean'
                    ? evalBlank.isCorrect
                    : evalSelectedIndex !== null && evalCorrectIndex !== null && evalSelectedIndex === evalCorrectIndex;
                const grade = (evalBlank.grade as string | undefined) ?? (isCorrect ? 'perfect' : undefined);
                const reason =
                  typeof evalBlank.reason === 'string' && evalBlank.reason.trim().length > 0 ? evalBlank.reason : null;

                const selectedLabel =
                  evalSelectedIndex !== null && evalSelectedIndex >= 0
                    ? options[evalSelectedIndex]?.label ?? selected?.label ?? '\u2014'
                    : selected?.label ?? '\u2014';
                const correctLabel =
                  evalCorrectIndex !== null && evalCorrectIndex >= 0
                    ? options[evalCorrectIndex]?.label ?? correct?.label ?? '\u2014'
                    : correct?.label ?? '\u2014';

                return (
                  <BlankResultCard
                    key={blank.blankId ?? `${sentenceIdx}-${blankIdx}`}
                    blankIndex={blankIdx}
                    providedAnswer={selectedLabel ?? '\u2014'}
                    expectedAnswer={correctLabel ?? '\u2014'}
                    grade={grade}
                    isCorrect={isCorrect}
                    reason={reason}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// File upload
// ---------------------------------------------------------------------------

function FileUploadSubmissionView({ submission }: { submission: any }) {
  const files = Array.isArray(submission.files) ? submission.files : [];
  const comment = typeof submission.comment === 'string' ? submission.comment.trim() : '';
  const [previewFile, setPreviewFile] = useState<PreviewableFile | null>(null);

  return (
    <div className="space-y-3">
      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Submitted Files</p>
          <div className="space-y-2">
            {files.map((file: any, idx: number) => (
              <div key={file.url ?? idx} className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-gray-100 shrink-0">
                  {getFileTypeIcon(file.contentType)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.filename ?? 'Unknown file'}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.fileSize)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreviewFile(file)}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 transition-colors"
                >
                  <Eye className="h-3.5 w-3.5" />
                  View
                </button>
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 rounded-md bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Download
                </a>
              </div>
            ))}
          </div>
          <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
        </div>
      )}
      {comment && (
        <div className="space-y-1 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Comment</p>
          <p className="whitespace-pre-wrap">{comment}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function SentenceHeader({
  index,
  sentence,
  title,
}: {
  index: number;
  sentence: string;
  title?: string | null;
}) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Sentenca {index + 1}</p>
        <p className="text-gray-900">{sentence}</p>
      </div>
      {title && (
        <span className="text-[11px] text-gray-400 line-clamp-1 max-w-[280px]">Exercicio: {title}</span>
      )}
    </div>
  );
}

function BlankResultCard({
  blankIndex,
  providedAnswer,
  expectedAnswer,
  grade,
  isCorrect,
  reason,
}: {
  blankIndex: number;
  providedAnswer: string;
  expectedAnswer: string;
  grade: string | undefined;
  isCorrect: boolean;
  reason: string | null;
}) {
  const statusTheme = resolveGradeStatusTheme(grade, isCorrect);
  // Gender-aware label: the viewing student's own submissions are the ones
  // rendered here, so the viewer's gender resolves the addressing. Defaults to
  // the feminine form (women-first platform).
  const { gender } = useUserType();
  const answerLabel = gender === 'male' ? 'Resposta do aluno' : 'Resposta da aluna';

  return (
    <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-500">Lacuna {blankIndex + 1}</p>
        <Badge variant="outline" className={statusTheme.badge}>
          {statusTheme.label}
        </Badge>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 text-xs text-gray-600 sm:grid-cols-2">
        <div className="rounded-md border border-gray-200 bg-white px-2 py-1.5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">{answerLabel}</p>
          <p className="font-medium text-gray-900">{providedAnswer}</p>
        </div>
        <div className="rounded-md border border-gray-200 bg-white px-2 py-1.5">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-500">Resposta esperada</p>
          <p className="font-medium text-gray-900">{expectedAnswer}</p>
        </div>
      </div>
      {reason && (
        <p className="mt-2 rounded-md border border-amber-100 bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Feedback da IA: {reason}
        </p>
      )}
    </div>
  );
}
